/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
import {
  CONTENT_TYPE_JSON,
  TOKEN_EXPIRED_MESSAGE,
} from "../../utils/apiConstants.js";
import { logger, sanitizeError } from "../../utils/logger.js";
/**
 * @fileoverview Partners Image logic module.
 * Handles CRUD operations for partner images displayed on the platform.
 * @module logic/partnersImage
 */

import { refreshToken, signOut } from "../../utils/common.js";

/**
 * Fetches all partner images from the API.
 * @async
 * @param {Function} navigate - React Router navigation function
 * @returns {Promise<Array>} Array of partner image objects
 */
export const getPartnersImage = async (navigate) => {
  const options = {
    method: "GET",
    headers: {
      "Content-Type": CONTENT_TYPE_JSON,
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
  };

  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}partnersImage`,
      options,
    );
    const data = await response.json();
    if (data?.message === TOKEN_EXPIRED_MESSAGE) {
      const newTokens = await refreshToken();
      if (!newTokens) {
        await signOut();
        navigate("/");
        return [];
      }
      return await getPartnersImage(navigate);
    }

    return data.data;
  } catch (error) {
    logger.error("Error fetching partners:", { err: sanitizeError(error) });
    return [];
  }
};

/**
 * Creates a new partner image.
 * @async
 * @param {Object} partnersImageData - Partner image data to create
 * @param {string} partnersImageData.partnersImageFileName - Image file name
 * @param {Function} navigate - React Router navigation function
 * @returns {Promise<Object|null>} Created partner image data or null on error
 */
export const createPartnersImage = async (partnersImageData, navigate) => {
  logger.info("Partners image data received to create:", {
    data: partnersImageData,
  });

  const options = {
    method: "POST",
    headers: {
      "Content-Type": CONTENT_TYPE_JSON,
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
    body: JSON.stringify(partnersImageData),
  };

  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}partnersImage`,
      options,
    );
    const data = await response.json();

    if (data?.message === TOKEN_EXPIRED_MESSAGE) {
      const newTokens = await refreshToken();
      if (!newTokens) {
        await signOut();
        navigate("/");
        return null;
      }
      return await createPartnersImage(partnersImageData, navigate);
    }

    return data;
  } catch (error) {
    logger.error("Error creating partner image:", {
      err: sanitizeError(error),
    });
    return null;
  }
};

/**
 * Updates an existing partner image.
 * @async
 * @param {string} partnersImageId - Partner image identifier
 * @param {Object} partnersImageData - Updated partner image data
 * @param {string} partnersImageData.partnersImageFileName - New image file name
 * @param {Function} navigate - React Router navigation function
 * @returns {Promise<Object|null>} Updated partner image data or null on error
 */
export const updatePartnersImage = async (
  partnersImageId,
  partnersImageData,
  navigate,
) => {
  logger.info("Partners image data received to update:", {
    data: partnersImageData,
  });

  const options = {
    method: "PUT",
    headers: {
      "Content-Type": CONTENT_TYPE_JSON,
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
    body: JSON.stringify({
      partnersImageFileName: partnersImageData.partnersImageFileName,
    }),
  };

  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}partnersImage/${partnersImageId}`,
      options,
    );
    const data = await response.json();

    if (data?.message === TOKEN_EXPIRED_MESSAGE) {
      const newTokens = await refreshToken();
      if (!newTokens) {
        await signOut();
        navigate("/");
        return null;
      }
      return await updatePartnersImage(
        partnersImageId,
        partnersImageData,
        navigate,
      );
    }

    return data;
  } catch (error) {
    logger.error("Error updating partner image:", {
      err: sanitizeError(error),
    });
    return null;
  }
};

/**
 * Deletes a partner image.
 * @async
 * @param {string} partnersImageId - Partner image identifier
 * @param {Function} navigate - React Router navigation function
 * @returns {Promise<boolean>} True if deletion was successful
 */
export const deletePartnersImage = async (partnersImageId, navigate) => {
  const options = {
    method: "DELETE",
    headers: {
      "Content-Type": CONTENT_TYPE_JSON,
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
  };

  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}partnersImage/${partnersImageId}`,
      options,
    );
    const data = await response.json();

    if (data?.message === TOKEN_EXPIRED_MESSAGE) {
      const newTokens = await refreshToken();
      if (!newTokens) {
        await signOut();
        navigate("/");
        return false;
      }
      return await deletePartnersImage(partnersImageId, navigate);
    }

    return response.ok;
  } catch (error) {
    logger.error("Error deleting partner image:", {
      err: sanitizeError(error),
    });
    return false;
  }
};
